import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { Eye, EyeOff, Shield } from 'lucide-react';
import { z } from 'zod';

const loginSchema = z.object({
  email: z.string().min(1, 'El usuario es requerido').email('Formato de correo inválido').max(255),
  password: z.string().min(6, 'La contraseña debe tener al menos 6 caracteres').max(72),
});

const Auth = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [showPinPad, setShowPinPad] = useState(false);
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState(false);
  const { signIn, user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    if (user) navigate('/admin');
  }, [user, navigate]);

  const validateForm = (): boolean => {
    try {
      loginSchema.parse({ email, password });
      setErrors({});
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: { email?: string; password?: string } = {};
        error.errors.forEach((err) => {
          if (err.path[0] === 'email') fieldErrors.email = err.message;
          else if (err.path[0] === 'password') fieldErrors.password = err.message;
        });
        setErrors(fieldErrors);
      }
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      toast({ title: 'Error de validación', description: 'Por favor corrige los errores en el formulario', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      const { error } = await signIn(email, password);
      if (error) {
        let errorMessage = 'Credenciales incorrectas';
        if (error.message.includes('Invalid login credentials') || error.message.includes('Invalid credentials')) {
          errorMessage = 'Credenciales incorrectas';
        }
        toast({ title: 'Error', description: errorMessage, variant: 'destructive' });
      } else {
        navigate('/admin');
      }
    } catch {
      toast({ title: 'Error', description: 'Ocurrió un error inesperado', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handlePinDigit = (digit: string) => {
    if (pin.length >= 4) return;

    const nextPin = pin + digit;
    setPin(nextPin);
    setPinError(false);

    if (nextPin.length !== 4) return;

    const savedPin = localStorage.getItem('kioskExitPin') || '7987';
    if (nextPin === savedPin) {
      setShowPinPad(false);
      setPin('');
      window.electronAPI?.toggleKiosk();
      return;
    }

    setPinError(true);
    window.setTimeout(() => setPin(''), 600);
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative" style={{ backgroundColor: 'hsl(var(--background))' }}>
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Iniciar Sesión</CardTitle>
          <CardDescription>Ingresa tus credenciales para acceder al panel de administración</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Usuario</Label>
              <Input
                id="email"
                type="email"
                placeholder="usuario@ejemplo"
                value={email}
                onChange={(e) => { setEmail(e.target.value); if (errors.email) setErrors(p => ({ ...p, email: undefined })); }}
                disabled={loading}
                required
                className={errors.email ? 'border-destructive' : ''}
              />
              {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Contraseña</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); if (errors.password) setErrors(p => ({ ...p, password: undefined })); }}
                  disabled={loading}
                  required
                  className={`pr-10 ${errors.password ? 'border-destructive' : ''}`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors.password && <p className="text-sm text-destructive">{errors.password}</p>}
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Acceso discreto de mantenimiento, visible solo dentro de KioscoPlayer. */}
      {window.electronAPI && (
        <button
          type="button"
          onClick={() => {
            setShowPinPad(true);
            setPin('');
            setPinError(false);
          }}
          className="absolute bottom-4 right-4 p-2 rounded-full text-muted-foreground/30 hover:text-muted-foreground/60 transition-colors"
          title="Mantenimiento"
          aria-label="Abrir mantenimiento"
          tabIndex={-1}
        >
          <Shield size={20} />
        </button>
      )}

      {showPinPad && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/80 backdrop-blur-sm z-50">
          <Card className="w-72 shadow-2xl">
            <CardHeader className="pb-2 text-center">
              <CardTitle className="text-lg">Salir del modo kiosco</CardTitle>
              <CardDescription>Introduce el PIN de técnico</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div
                className={`flex justify-center gap-3 transition-transform ${
                  pinError ? 'animate-bounce' : ''
                }`}
              >
                {[0, 1, 2, 3].map((index) => (
                  <div
                    key={index}
                    className={`w-4 h-4 rounded-full border-2 transition-colors ${
                      index < pin.length
                        ? pinError
                          ? 'bg-destructive border-destructive'
                          : 'bg-foreground border-foreground'
                        : 'border-muted-foreground/40'
                    }`}
                  />
                ))}
              </div>

              <div className="grid grid-cols-3 gap-2">
                {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((digit) => (
                  <button
                    key={digit}
                    type="button"
                    onClick={() => handlePinDigit(digit)}
                    className="h-14 text-xl font-semibold rounded-lg border border-border hover:bg-muted transition-colors"
                  >
                    {digit}
                  </button>
                ))}
                <div />
                <button
                  type="button"
                  onClick={() => handlePinDigit('0')}
                  className="h-14 text-xl font-semibold rounded-lg border border-border hover:bg-muted transition-colors"
                >
                  0
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setPin((currentPin) => currentPin.slice(0, -1));
                    setPinError(false);
                  }}
                  className="h-14 text-xl font-semibold rounded-lg border border-border hover:bg-muted transition-colors text-muted-foreground"
                  aria-label="Borrar último dígito"
                >
                  ⌫
                </button>
              </div>

              <Button
                type="button"
                variant="ghost"
                className="w-full"
                onClick={() => {
                  setShowPinPad(false);
                  setPin('');
                  setPinError(false);
                }}
              >
                Cancelar
              </Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Auth;
